const hasText = (value) => String(value || "").trim().length > 0;
const hasItems = (value) => Array.isArray(value) && value.length > 0;

const required = (label, done) => ({ label, done, required: true });
const recommended = (label, done) => ({ label, done, required: false });

export const SITE_SECTION_META = {
  identity: {
    label: "Datos e identidad",
    description: "Nombre, descripción y estado general del negocio.",
  },
  navbar: {
    label: "Barra de navegación",
    description: "Logo, accesos principales y estilo del encabezado que acompaña a toda la web.",
  },
  hero: {
    label: "Portada principal",
    description: "La primera impresión: propuesta de valor, imagen principal y llamados a la acción.",
  },
  spotlight: {
    label: "Bloque destacado",
    description: "Una campaña, marca o servicio especial con identidad propia dentro de la página.",
  },
  featured: {
    label: "Productos destacados",
    description: "Presentación de los productos o servicios que querés priorizar.",
  },
  brands: {
    label: "Marcas",
    description: "Logos y accesos rápidos para ordenar el catálogo por marca.",
  },
  categories: {
    label: "Categorías",
    description: "Ayuda a que las personas encuentren más rápido lo que están buscando.",
  },
  about: {
    label: "Sobre el negocio",
    description: "Historia, experiencia y una imagen real que genere confianza.",
  },
  highlights: {
    label: "Diferenciales",
    description: "Beneficios concretos que explican por qué elegir este negocio.",
  },
  contact: {
    label: "Contacto",
    description: "Canales de consulta, ubicación, horarios y acceso directo a WhatsApp.",
  },
  footer: {
    label: "Pie de página",
    description: "Cierre de la web con identidad, navegación y datos esenciales.",
  },
  floatingWhatsapp: {
    label: "WhatsApp flotante",
    description: "Acceso rápido para convertir visitas en consultas.",
  },
  seo: {
    label: "SEO y publicación",
    description: "Título para Google, descripción, imagen para compartir y estado público.",
  },
};

export const SITE_BUILDER_STEPS = [
  {
    id: "identity",
    number: 1,
    label: "Identidad visual",
    shortLabel: "Identidad",
    description: "Definí el nombre, la personalidad visual y los recursos principales.",
    actions: [
      { id: "identityContent", label: "Nombre y descripción", description: "Lo básico que identifica al negocio.", type: "section", section: "identity" },
      { id: "appearance", label: "Colores, tipografía y estilos", description: "La paleta se aplica a botones, títulos, fondos y textos.", type: "settings", tab: 0 },
      { id: "assets", label: "Logos e imágenes", description: "Cargá archivos reales sin pegar URLs técnicas.", type: "settings", tab: 1 },
    ],
    getChecks: (form) => [
      required("Nombre del negocio", hasText(form.business?.name)),
      required("Descripción breve", hasText(form.business?.shortDescription)),
      required("Logo principal", hasText(form.theme?.logoUrl)),
      required("Paleta definida", hasText(form.theme?.primaryColor) && hasText(form.theme?.secondaryColor)),
      recommended("Favicon", hasText(form.theme?.faviconUrl)),
    ],
  },
  {
    id: "cover",
    number: 2,
    label: "Portada y navegación",
    shortLabel: "Portada",
    description: "Construí una primera pantalla clara y orientada a la acción.",
    actions: [
      { id: "navbar", label: "Barra de navegación", description: "Elegí qué accesos ve el visitante.", type: "section", section: "navbar" },
      { id: "hero", label: "Portada principal", description: "Título, mensaje, botones y estructura del carrusel.", type: "section", section: "hero" },
      { id: "heroAssets", label: "Imágenes de portada", description: "Subí la imagen principal o armá un slider.", type: "settings", tab: 1 },
    ],
    getChecks: (form) => [
      required("Título principal", hasText(form.hero?.title)),
      required("Texto de apoyo", hasText(form.hero?.subtitle)),
      required(
        "Imagen o slider",
        hasText(form.hero?.imageUrl) ||
          (Array.isArray(form.hero?.slides) && form.hero.slides.some((slide) => hasText(slide?.url)))
      ),
      required("Botón principal", hasText(form.hero?.primaryCtaLabel)),
      recommended("Segundo botón", hasText(form.hero?.secondaryCtaLabel)),
    ],
  },
  {
    id: "catalog",
    number: 3,
    label: "Catálogo",
    shortLabel: "Catálogo",
    description: "Ordená cómo se presentan productos, servicios, marcas y categorías.",
    requiresProducts: true,
    actions: [
      { id: "featured", label: "Productos o servicios destacados", description: "Definí el mensaje del bloque principal.", type: "section", section: "featured" },
      { id: "categories", label: "Categorías", description: "Ayudá a encontrar el catálogo más rápido.", type: "section", section: "categories" },
      { id: "brands", label: "Marcas", description: "Mostrá marcas y accesos filtrados.", type: "section", section: "brands" },
      { id: "spotlight", label: "Campaña o bloque especial", description: "Destacá una promoción, rubro o servicio.", type: "section", section: "spotlight" },
    ],
    getChecks: (form) => {
      const visibleCatalog = form.sections?.showProducts === true || form.sections?.showFeatured === true;
      const organized =
        hasText(form.catalog?.featured?.title) ||
        hasText(form.catalog?.categories?.title) ||
        hasItems(form.catalog?.brands?.items) ||
        (form.catalog?.spotlight?.enabled === true && hasText(form.catalog?.spotlight?.title));
      return [
        required("Catálogo visible", visibleCatalog),
        required("Mensaje principal del catálogo", hasText(form.catalog?.featured?.title)),
        required("Catálogo organizado", organized),
        recommended(
          "Marcas cargadas",
          Array.isArray(form.catalog?.brands?.items) &&
            form.catalog.brands.items.some((item) => hasText(item?.name) || hasText(item?.logoUrl))
        ),
        recommended("Campaña destacada", form.catalog?.spotlight?.enabled === true && hasText(form.catalog?.spotlight?.title)),
      ];
    },
  },
  {
    id: "trust",
    number: 4,
    label: "Historia y confianza",
    shortLabel: "Confianza",
    description: "Contá quién está detrás y por qué deberían elegirte.",
    actions: [
      { id: "about", label: "Sobre nosotros", description: "Historia, experiencia y propuesta del negocio.", type: "section", section: "about" },
      { id: "highlights", label: "Por qué elegirnos", description: "Cargá beneficios concretos y fáciles de leer.", type: "section", section: "highlights" },
      { id: "trustAssets", label: "Imágenes institucionales", description: "Sumá una foto del negocio y de los diferenciales.", type: "settings", tab: 1 },
    ],
    getChecks: (form) => [
      required("Título institucional", hasText(form.about?.title)),
      required("Historia o descripción", hasText(form.about?.description)),
      required(
        "Dos diferenciales claros",
        Array.isArray(form.highlights) &&
          form.highlights.filter((item) => hasText(item?.title) && hasText(item?.description)).length >= 2
      ),
      recommended("Imagen del negocio", hasText(form.about?.imageUrl)),
    ],
  },
  {
    id: "contact",
    number: 5,
    label: "Contacto y cierre",
    shortLabel: "Contacto",
    description: "Facilitá la consulta y cerrá la página con información útil.",
    actions: [
      { id: "contact", label: "Sección de contacto", description: "Título, mensaje y llamado a la acción.", type: "section", section: "contact" },
      { id: "floatingWhatsapp", label: "WhatsApp flotante", description: "Definí el mensaje que inicia la conversación.", type: "section", section: "floatingWhatsapp" },
      { id: "footer", label: "Pie de página", description: "Cerrá la web con identidad y accesos útiles.", type: "section", section: "footer" },
      { id: "contactData", label: "Datos públicos y redes", description: "Se reutilizan automáticamente en toda la web.", type: "settings", tab: 3 },
    ],
    getChecks: (form) => [
      required(
        "Canal de contacto",
        hasText(form.business?.whatsapp) || hasText(form.business?.phone) || hasText(form.business?.email)
      ),
      required("Título de contacto", hasText(form.contact?.title)),
      required("Mensaje de WhatsApp", hasText(form.contact?.whatsappMessage)),
      recommended("Dirección o ciudad", hasText(form.business?.address) || hasText(form.business?.city)),
      recommended("Horarios", hasText(form.business?.schedule)),
    ],
  },
  {
    id: "publish",
    number: 6,
    label: "Revisión y publicación",
    shortLabel: "Publicar",
    description: "Revisá lo esencial y cerrá el proceso con la web publicada.",
    actions: [
      { id: "visibility", label: "Secciones visibles", description: "Activá solo lo que el negocio realmente necesita.", type: "settings", tab: 2 },
      { id: "publishSettings", label: "SEO y publicación", description: "Completá Google y elegí el estado público.", type: "settings", tab: 4 },
    ],
    getChecks: (form) => [
      required("Título para Google", hasText(form.seo?.title)),
      required("Descripción para Google", hasText(form.seo?.description)),
      required("Sitio publicado", form.publicStatus === "published"),
      recommended("Imagen para compartir", hasText(form.seo?.imageUrl)),
    ],
  },
];

export const SITE_BUILDER_SIDEBAR_STEPS = SITE_BUILDER_STEPS.map(
  ({ id, number, shortLabel, requiresProducts }) => ({ id, number, label: shortLabel, requiresProducts })
);

export function getAvailableSiteBuilderSteps(productsEnabled = true) {
  return SITE_BUILDER_STEPS.filter((step) => !step.requiresProducts || productsEnabled);
}

export function getSiteBuilderProgress(form = {}, productsEnabled = true) {
  const steps = getAvailableSiteBuilderSteps(productsEnabled).map((step) => {
    const checks = step.getChecks(form);
    const essentialChecks = checks.filter((item) => item.required !== false);
    const recommendedChecks = checks.filter((item) => item.required === false);
    const completed = essentialChecks.filter((item) => item.done).length;
    const total = Math.max(1, essentialChecks.length);
    const recommendedCompleted = recommendedChecks.filter((item) => item.done).length;

    return {
      ...step,
      checks,
      completed,
      total,
      recommendedCompleted,
      recommendedTotal: recommendedChecks.length,
      percentage: Math.round((completed / total) * 100),
      done: completed === total,
    };
  });

  const completed = steps.reduce((sum, step) => sum + step.completed, 0);
  const total = steps.reduce((sum, step) => sum + step.total, 0) || 1;
  const recommendedCompleted = steps.reduce((sum, step) => sum + step.recommendedCompleted, 0);
  const recommendedTotal = steps.reduce((sum, step) => sum + step.recommendedTotal, 0);

  return {
    steps,
    completed,
    total,
    recommendedCompleted,
    recommendedTotal,
    percentage: Math.round((completed / total) * 100),
    complete: completed === total,
  };
}

export function findBuilderStep(stepId, productsEnabled = true) {
  const steps = getAvailableSiteBuilderSteps(productsEnabled);
  return steps.find((step) => step.id === stepId) || steps[0];
}

export function findStepForSection(sectionId, productsEnabled = true) {
  return getAvailableSiteBuilderSteps(productsEnabled).find((step) =>
    step.actions.some((action) => action.section === sectionId)
  );
}
